import HighNoise from "@audio/high_short.mp3";
import LowNoise from "@audio/low_short.mp3";
import viewstyle from "@components/organisms/DtgView/style.module.css";
import { Trans, t } from "@lingui/macro";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { Stack, Typography } from "@mui/material";
import EndScreen from "@root/components/molecules/EndScreen/Component";
import { HCAlert } from "@root/components/molecules/HCAlert/Component";
import Introduction from "@root/components/molecules/Introduction/Component";
import { DtgView } from "@root/components/organisms/DtgView/Component";
import {
  DtgColorsPractise,
  DtgPedalsPractise,
  DtgSoundPractise,
} from "@root/components/organisms/DtgViewPractise/Component";
import { DtgViewReaction } from "@root/components/organisms/DtgViewReaction/Component";
import { useAppContext } from "@root/global/context";
import { ProgressStateDtg } from "@root/misc/constants";
import { checkTitle, hasValue } from "@root/misc/helpers";
import { Tone } from "@root/misc/types";
import YipYipButtons from "@root/misc/YipYipButtons";
import clsx from "clsx";
import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

const possibleKeys = ["F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9"];

const DtgInstructions = (): JSX.Element => {
  const [pressedKey, setPressedKey] = React.useState<string | null>();
  const [isLeftPedalF8] = useLocalStorage<string | null>("isLeftPedalF8", null);
  const [currentTone, setCurrentTone] = React.useState<Tone | null>(null);
  const { setProgressStateDtg } = useAppContext();
  const { variant } = useParams();
  const location = useLocation();

  const playAudio = (tone: Tone): void => {
    setCurrentTone((old) => {
      return old === tone ? null : tone;
    });
  };

  const returnTone = (): string => {
    if (currentTone === Tone.high) {
      return HighNoise;
    }
    return LowNoise;
  };

  React.useEffect(() => {
    if (pressedKey === "f6") {
      playAudio(Tone.low);
    }
    if (pressedKey === "f7") {
      playAudio(Tone.high);
    }
  }, [pressedKey]);

  const handleUserKeyPress = React.useCallback(
    (event: KeyboardEvent): void => {
      event.preventDefault();

      if (event.repeat) {
        return;
      }

      if (possibleKeys.includes(event.key)) {
        const { key } = event;
        const lowerKey = key.toLowerCase();
        setPressedKey(lowerKey);
      }
    },
    [isLeftPedalF8],
  );

  const handleUserKeyRelease = React.useCallback(
    (event: KeyboardEvent): void => {
      if (event.repeat) {
        return;
      }

      if (possibleKeys.includes(event.key)) {
        event.preventDefault();
        const { key } = event;
        const lowerKey = key.toLowerCase();
        setPressedKey((old) => (lowerKey === old ? null : old));
      }
    },
    [isLeftPedalF8],
  );

  React.useEffect(() => {
    window.addEventListener("keydown", handleUserKeyPress);
    window.addEventListener("keyup", handleUserKeyRelease);
    return () => {
      window.removeEventListener("keydown", handleUserKeyPress);
      window.removeEventListener("keyup", handleUserKeyRelease);
    };
  }, [handleUserKeyPress, handleUserKeyRelease]);

  return (
    <Introduction
      key="dtg3"
      prevFn={() => setProgressStateDtg(ProgressStateDtg.InstructionPart1Page1)}
      nextFn={() => setProgressStateDtg(ProgressStateDtg.PractisePart1)}
      title={checkTitle(location.pathname)}
      nextButtonLabel={t({ id: "dtg.instruction.title.page2", message: "Oefentest beginnen" })}
      part={2}
      description={
        <>
          <p>
            <Trans id="dtg.instruction.page2.onlocation">
              <strong>De test bestaat uit twee delen.</strong>
              <br />
              <br />
              In het eerste deel verschijnen de signalen met een vast tempo. Tijdens de test wordt
              dit tempo twee keer verhoogd. Probeer steeds zo goed mogelijk te reageren, ook wanneer
              je een aantal signalen mist.
            </Trans>
          </p>
          <Typography variant="h2" sx={{ marginBlockEnd: 2 }}>
            <Trans id="dtg.instruction.page2.onlocation.sound-signal-heading">Geluidsignalen</Trans>
          </Typography>

          {hasValue(currentTone) && (
            <audio src={returnTone()} loop autoPlay onEnded={() => setCurrentTone(null)} />
          )}

          <Stack sx={{ height: "80px", flex: "0 0 80px" }} direction="row" spacing={2}>
            <YipYipButtons.ButtonBlack
              variant="contained"
              onClick={() => playAudio(Tone.low)}
              sx={variant === "on-location" ? { maxHeight: "60px" } : {}}
            >
              {hasValue(currentTone) && currentTone === Tone.low ? (
                <Trans id="general.tone.stop">Stop audio</Trans>
              ) : (
                <Trans id="general.tone.low">Lage tonen</Trans>
              )}
            </YipYipButtons.ButtonBlack>
            <YipYipButtons.ButtonBlack
              variant="contained"
              onClick={() => playAudio(Tone.high)}
              sx={variant === "on-location" ? { maxHeight: "60px" } : {}}
            >
              {hasValue(currentTone) && currentTone === Tone.high ? (
                <Trans id="general.tone.stop">Stop audio</Trans>
              ) : (
                <Trans id="general.tone.high">Hoge tonen</Trans>
              )}
            </YipYipButtons.ButtonBlack>
          </Stack>

          {variant === "on-location" && (
            <>
              <Typography variant="h2" sx={{ marginBlockStart: "8px", marginBlockEnd: "8px" }}>
                <Trans id="dtg.test.peddals">
                  Deze elementen geven aan dat er een pedaal moet worden ingedrukt.
                </Trans>
              </Typography>

              <Stack
                sx={{ height: "80px", flex: "0 0 80px" }}
                direction="row"
                justifyContent="space-between"
                spacing={2}
              >
                <div
                  className={clsx(
                    viewstyle["left-square"],
                    hasValue(isLeftPedalF8) &&
                      isLeftPedalF8 === "true" &&
                      pressedKey === "f8" &&
                      viewstyle["left-square-active"],
                    hasValue(isLeftPedalF8) &&
                      isLeftPedalF8 === "false" &&
                      pressedKey === "f9" &&
                      viewstyle["left-square-active"],
                  )}
                >
                  L
                </div>
                <div
                  className={clsx(
                    viewstyle["right-square"],
                    hasValue(isLeftPedalF8) &&
                      isLeftPedalF8 === "true" &&
                      pressedKey === "f9" &&
                      viewstyle["right-square-active"],
                    hasValue(isLeftPedalF8) &&
                      isLeftPedalF8 === "false" &&
                      pressedKey === "f8" &&
                      viewstyle["right-square-active"],
                  )}
                >
                  R
                </div>
              </Stack>
            </>
          )}
        </>
      }
    />
  );
};

// For each different step in a test, this file renders the HTML for that specific step.
export function Dtg(): JSX.Element {
  const { progressStateDtg, setProgressStateDtg, dtgData } = useAppContext();
  const location = useLocation();
  const { variant } = useParams();

  if (progressStateDtg === ProgressStateDtg.Introduction) {
    return (
      <Introduction
        key="dtg1"
        part={0}
        nextFn={() =>
          variant === "on-location"
            ? setProgressStateDtg(ProgressStateDtg.OnLocationSoundInstruction)
            : setProgressStateDtg(ProgressStateDtg.InstructionPart1Page1)
        }
        title={checkTitle(location.pathname)}
        nextButtonLabel={t({ id: "dtg.introduction.title", message: "Instructies lezen" })}
        description={
          <>
            <p>
              <Trans id="dtg.introduction">
                Bij deze test gaat het om het reactievermogen; het vermogen om zonder aarzelen
                adequaat te reageren op bepaalde signalen.
              </Trans>
            </p>
            <p>
              <Trans id="gbd.introduction.subtext">
                <strong>
                  Lees de instructie goed door en start de test pas als u deze volledig hebt
                  begrepen.
                </strong>
              </Trans>
            </p>
          </>
        }
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.InstructionPart1Page1) {
    return (
      <Introduction
        key="dtg2"
        nextFn={() => setProgressStateDtg(ProgressStateDtg.InstructionPart1Page2)}
        title={checkTitle(location.pathname)}
        nextButtonLabel={t({ id: "dtg.instruction.title.page1", message: "Volgende pagina" })}
        part={1}
        description={
          <>
            {variant === "on-location" && (
              <>
                <Typography variant="body1">
                  <strong>
                    <br />
                    <Trans id="dtg.instruction.page1.onlocation.warning">
                      Je gebruikt bij deze test het toetsenbord, een koptelefoon en de pedalen.
                    </Trans>
                  </strong>
                </Typography>
                <br />
                <Typography variant="body1">
                  <Trans id="dtg.instruction.page1.onlocation">
                    Je moet straks zo snel en goed mogelijk gaan reageren op signalen die je krijgt.
                    Dit zijn licht- en geluidsignalen. Je reageert op de verschillende signalen op
                    de volgende manier:
                  </Trans>
                </Typography>

                <br />
                <Typography variant="h2">
                  <Trans id="dtg.instruction.page1.onlocation.light-signal-heading">
                    Lichtsignalen
                  </Trans>
                </Typography>

                <p>
                  <Typography variant="body1">
                    <Trans id="dtg.instruction.page1.onlocation.light-signal-explanation">
                      De lichtsignalen verschijnen als gekleurde stip op het scherm. Druk op de
                      overeenkomende kleur op het toetsenbord.
                    </Trans>
                  </Typography>
                </p>

                <Typography variant="h2">
                  <Trans id="dtg.instruction.page1.onlocation.sound-signal-heading">
                    Geluidsignalen
                  </Trans>
                </Typography>

                <p>
                  <Typography variant="body1">
                    <Trans id="dtg.instruction.page1.onlocation.sound-signal-explanation">
                      De geluidsignalen zijn een hoge of lage toon die worden afgespeeld. Druk op de
                      “H” toets bij een hoge toon en op de “L” toets bij een lage toon.
                    </Trans>
                  </Typography>
                </p>
              </>
            )}
            {variant !== "on-location" && (
              <>
                <Typography variant="body1">
                  <strong>
                    <br />
                    <Trans id="dtg.instruction.page1.home.warning">
                      Je gebruikt bij deze test het toetsenbord, een koptelefoon en de pedalen.
                    </Trans>
                  </strong>
                </Typography>
                <br />
                <Typography variant="body1">
                  <Trans id="dtg.instruction.page1.home">
                    Je moet straks zo snel en goed mogelijk gaan reageren op signalen die je krijgt.
                    Dit zijn licht- en geluidsignalen. Je reageert op de verschillende signalen op
                    de volgende manier:
                  </Trans>
                </Typography>

                <br />
                <Typography variant="h2">
                  <Trans id="dtg.instruction.page1.home.light-signal-heading">Lichtsignalen</Trans>
                </Typography>

                <p>
                  <Typography variant="body1">
                    <Trans id="dtg.instruction.page1.home.light-signal-explanation">
                      De lichtsignalen verschijnen als gekleurde stip op het scherm. Onderin beeld
                      staan een serie knoppen met de kleuren. Klik met je muis op de knop van de
                      overeenkomende kleur.
                    </Trans>
                  </Typography>
                </p>

                <Typography variant="h2">
                  <Trans id="dtg.instruction.page1.home.sound-signal-heading">Geluidsignalen</Trans>
                </Typography>

                <p>
                  <Typography variant="body1">
                    <Trans id="dtg.instruction.page1.home.sound-signal-explanation">
                      De geluidsignalen zijn een hoge of lage toon die worden afgespeeld. Onderin
                      beeld staan twee knoppen voor de hoge en lage toon. Klik met je muis op de
                      knop van de overeenkomende toon.
                    </Trans>
                  </Typography>
                </p>
              </>
            )}
            <HCAlert
              kind="info"
              icon={<VolumeUpIcon sx={{ color: "var(--color-brand-secondary-500)" }} />}
            >
              <Trans id="dtg.instruction.page1.tip">Tip: Zorg dat je geluid aanstaat</Trans>
            </HCAlert>
          </>
        }
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.InstructionPart1Page2) {
    return <DtgInstructions />;
  }

  if (progressStateDtg === ProgressStateDtg.InstructionPart2) {
    return (
      <Introduction
        nextFn={() => setProgressStateDtg(ProgressStateDtg.TestPart2)}
        title={t({ id: "dtg.instruction.part2.title.title", message: "Test deel twee" })}
        nextButtonLabel={t({
          id: "dtg.instruction.part2.title.page1",
          message: "Tweede testdeel beginnen",
        })}
        part={1}
        description={
          <p>
            <Trans id="dtg.instruction.part2">
              Nu volgt nog een tweede testdeel. Je krijgt nu pas een volgend signaal als je een
              reactie gegeven hebt. Je bepaalt dus je eigen tempo. Werk zo snel en nauwkeurig
              mogelijk.
              <br />
              <br />
              De bediening en knoppen zijn hetzelfde als het vorige testdeel.
            </Trans>
          </p>
        }
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.PractisePart1) {
    return (
      <DtgView
        key="practise"
        isPractise
        variant={variant ?? "on-location"}
        endFn={() => setProgressStateDtg(ProgressStateDtg.TestPart1)}
      />
    );
  }

  // if (progressStateDtg === ProgressStateDtg.PractisePart2) {
  //   return (
  //     <DtgViewReaction key='practise2' isPractise variant={variant ?? 'on-location'} endFn={() => setProgressStateDtg(ProgressStateDtg.TestPart2)} />
  //   )
  // }

  if (progressStateDtg === ProgressStateDtg.End) {
    return <EndScreen type="dtg" data={dtgData} variant={variant ?? ""} />;
  }

  if (progressStateDtg === ProgressStateDtg.TestPart1) {
    return (
      <DtgView
        key="test"
        variant={variant ?? "on-location"}
        endFn={() => setProgressStateDtg(ProgressStateDtg.InstructionPart2)}
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.OnLocationSoundInstruction) {
    return (
      <Introduction
        prevFn={() => setProgressStateDtg(ProgressStateDtg.Introduction)}
        nextFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationSoundPractise)}
        title={t({ id: "dtg.on-location.sound-instruction.title", message: "Geluidsignalen" })}
        nextButtonLabel={t({
          id: "dtg.on-location.practise.button",
          message: "Oefenen",
        })}
        part={1}
        description={
          <>
            <h2>
              <Trans id="dtg.on-location.sound-instruction.subtitle">Lage en hoge tonen</Trans>
            </h2>
            <p>
              <Trans id="dtg.on-location.sound-instruction.body">
                Je hoort straks een lage of hoge toon. Op het toetsenbord voor je zitten de “L” en
                “H” toets. Hoor je een lage toon, dan is het de bedoeling dat je op de “L” toets
                drukt. Bij een hoge toon druk je op de “H” toets.
              </Trans>
            </p>
            <br />
            <br />
            <HCAlert kind="info">
              <Trans id="dtg.on-location.sound-instruction.alert">
                Controleer gelijk of er geluid uit je hoofdtelefoon komt
              </Trans>
            </HCAlert>
          </>
        }
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.OnLocationSoundPractise) {
    return (
      <DtgSoundPractise
        nextFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationColorInstruction)}
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.OnLocationColorInstruction) {
    return (
      <Introduction
        prevFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationSoundInstruction)}
        nextFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationColorPractise)}
        title={t({ id: "dtg.on-location.color-instruction.title", message: "Kleursignalen" })}
        nextButtonLabel={t({
          id: "dtg.on-location.practise.button",
          message: "Oefenen",
        })}
        part={1}
        description={
          <>
            <h2>
              <Trans id="dtg.on-location.color-instruction.subtitle">Gekleurde stippen</Trans>
            </h2>
            <p>
              <Trans id="dtg.on-location.color-instruction.body">
                Er verschijnen straks gekleurde stippen op het scherm. Op je toetsenbord zitten vijf
                gekleurde knoppen. Het is de bedoeling dat je als reactie op de juiste kleur knop
                drukt op het toetsenbord.
              </Trans>
              <br />
            </p>
          </>
        }
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.OnLocationColorPractise) {
    return (
      <DtgColorsPractise
        nextFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationPedalInstruction)}
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.OnLocationPedalInstruction) {
    return (
      <Introduction
        prevFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationSoundInstruction)}
        nextFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationPedalPractise)}
        title={t({ id: "dtg.on-location.pedal-instruction.title", message: "Pedalen" })}
        nextButtonLabel={t({
          id: "dtg.on-location.practise.button",
          message: "Oefenen",
        })}
        part={1}
        description={
          <>
            <h2>
              <Trans id="dtg.on-location.pedal-instruction.subtitle">Links en rechts</Trans>
            </h2>
            <p>
              <Trans id="dtg.on-location.pedal-instruction.body">
                Er verschijnen straks rechthoeken aan de linker of rechterkant van het scherm. Onder
                de tafel vind je twee pedalen. Het is de bedoeling dat je met je linker en
                rechtervoet de juiste pedalen indrukt als deze in beeld verschijnen.
              </Trans>
              <br />
            </p>
            <HCAlert kind="info">
              <Trans id="dtg.on-location.pedal-instruction.alert">
                Zorg dat je makkelijk met je voeten bij de pedalen kan. Pas als het nodig is je
                houding aan.
              </Trans>
            </HCAlert>
          </>
        }
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.OnLocationPedalPractise) {
    return (
      <DtgPedalsPractise
        nextFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationFinalInstruction)}
      />
    );
  }

  if (progressStateDtg === ProgressStateDtg.OnLocationFinalInstruction) {
    return (
      <Introduction
        prevFn={() => setProgressStateDtg(ProgressStateDtg.OnLocationSoundInstruction)}
        nextFn={() => setProgressStateDtg(ProgressStateDtg.PractisePart1)}
        title={t({
          id: "dtg.on-location.final-instruction.title",
          message: "Klaar voor de oefentoets?",
        })}
        nextButtonLabel={t({
          id: "dtg.on-location.finalize",
          message: "Oefenentoets starten",
        })}
        part={1}
        description={
          <>
            <h2>
              <Trans id="dtg.on-location.final-instruction.subtitle">
                Je bent nu bekend met het bedienen van de toets.
              </Trans>
            </h2>
            <p>
              <Trans id="dtg.on-location.final-instruction.body">
                Gebruik de juiste knoppen en pedalen om op de verschillende signalen te reageren.
                Deze verschijnen nu in een willekeurige volgorde op het scherm.{" "}
              </Trans>
            </p>
          </>
        }
      />
    );
  }

  return (
    <DtgViewReaction
      key="test2"
      variant={variant ?? "on-location"}
      endFn={() => setProgressStateDtg(ProgressStateDtg.End)}
    />
  );
}
